/**
 * useProspectClaim — Card 6a333d48 / F2 (merge-on-login).
 *
 * Hook que reclama el ``prospect`` anónimo del usuario al backend apenas
 * detecta que se autenticó (Auth0). El backend (POST /api/v1/prospects/claim)
 * setea ``prospect.user_id`` para que el contexto del wizard / diagnóstico
 * NO se pierda al loguearse.
 *
 * Dependencias
 * ------------
 * - Auth0 (``useAuth0``): expone ``isAuthenticated`` post-login.
 * - ``chatUIStore.conversationId``: persistido en localStorage por la card
 *   6a333ce6 (PR #409). Es la session_id anónima que el backend matchea.
 *
 * Idempotencia
 * ------------
 * - ``sessionStorage['inbest-prospect-claimed-<conversationId>']``: flag
 *   por-tab para no reintentar dentro del mismo tab. El backend es la
 *   fuente de verdad (devuelve ``already_owned`` si se vuelve a llamar
 *   desde otro tab).
 *
 * Manejo de errores (silencioso por diseño)
 * -----------------------------------------
 * - **404**: anónimo no completó wizard, no hay prospect → log info, no-op.
 * - **409**: otro user ya reclamó esa session (race / device compartido)
 *   → log warn (para auditar piloto), no se muestra UI.
 * - **401**: el ``apiClient`` interceptor maneja el redirect a Auth0.
 *
 * Importante: en error 4xx/5xx el flag de sessionStorage NO se setea, así
 * un próximo render puede reintentar (ej. si hubo 5xx transitorio). En
 * 409 SÍ lo seteamos para no repetir el log warn en loop.
 */

import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { claimProspect } from '@/api/endpoints/prospects';
import { useChatUIStore } from '@/stores/chatUIStore';
import { logger } from '@/lib/logger';
import { isApiError } from '@/api/client';
import { emitProspectClaimed } from '@/analytics/authGateEvents';

const CLAIM_SS_KEY_PREFIX = 'inbest-prospect-claimed-';

/**
 * Lee defensivamente el flag de claim desde sessionStorage.
 * Private mode / SecurityError → trata como "no claimado".
 */
function hasClaimedFlag(conversationId: string): boolean {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return (
        window.sessionStorage.getItem(
          `${CLAIM_SS_KEY_PREFIX}${conversationId}`,
        ) === '1'
      );
    }
  } catch {
    // SecurityError → tratamos como no flag.
  }
  return false;
}

/**
 * Setea el flag de claim. Tolerante a private mode.
 */
function setClaimedFlag(conversationId: string): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(
        `${CLAIM_SS_KEY_PREFIX}${conversationId}`,
        '1',
      );
    }
  } catch {
    // Silenciosamente OK.
  }
}

/**
 * Hook con efecto silencioso: dispara ``POST /api/v1/prospects/claim`` la
 * primera vez que el user se loguea y tenemos un ``conversationId``
 * persistido.
 *
 * Mount: en ``TokenConfigProvider`` (debajo de ``Auth0Provider``).
 */
export function useProspectClaim(): void {
  const { isAuthenticated, isLoading, user: auth0User } = useAuth0();
  const conversationId = useChatUIStore((s) => s.conversationId);

  // Guard adicional contra doble dispatch: en StrictMode, el efecto puede
  // correr dos veces. ``sessionStorage`` cubre el caso entre renders, este
  // ref cubre el caso síncrono dentro del mismo render.
  const inFlightRef = useRef<string | null>(null);

  useEffect(() => {
    // Aún cargando Auth0 → esperar (sin tocar nada).
    if (isLoading) return;
    // No autenticado → no hay claim que hacer.
    if (!isAuthenticated) return;
    // Sin conversationId → no podemos matchear el prospect.
    if (!conversationId) return;
    // Ya claimado en este tab → no-op.
    if (hasClaimedFlag(conversationId)) return;
    // Claim en vuelo para esta misma session → evitar doble request.
    if (inFlightRef.current === conversationId) return;

    inFlightRef.current = conversationId;

    void (async () => {
      try {
        const result = await claimProspect(conversationId);
        // 200 (claimed | already_owned) → persistimos flag para no reintentar.
        setClaimedFlag(conversationId);
        logger.debug(
          `[useProspectClaim] outcome=${result.outcome} session_id=${conversationId}`,
        );
        // Card N52QMDjD — ``prospect_claimed`` cierra el loop del funnel
        // GA4. ``materialized=true`` cuando el backend recien creo el
        // prospect (outcome=``claimed``); false cuando ya existia
        // (``already_owned``, ej. segundo tab). El helper es idempotente
        // por session_id, asi que un re-render con el mismo conversationId
        // no re-emite. Sub tomado del ID token — nunca email.
        try {
          const sub = auth0User?.sub;
          if (typeof sub === 'string' && sub) {
            emitProspectClaimed(
              conversationId,
              sub,
              result.outcome === 'claimed',
            );
          }
        } catch {
          // Analytics jamas debe romper el hook. Ver comentario del catch
          // externo — un throw aca se convertiria en unhandled rejection.
        }
      } catch (err) {
        // Defensa total: el handler de error NO debe lanzar bajo ninguna
        // circunstancia. Un throw acá se convierte en unhandled rejection y
        // mata la suite de tests aunque todos los assertions hayan pasado.
        // Vimos este modo de falla en CI cuando un test mockeaba
        // ``@/api/client`` sin exponer ``isApiError`` — el getter del mock
        // arrojaba dentro del catch y rompía 732/732 OK → exit 1.
        try {
          const status = isApiError(err) ? err.status : undefined;
          if (status === 404) {
            // Anónimo sin wizard previo: no hay prospect a reclamar. Edge
            // benigno — marcamos flag para no reintentar (el resultado va
            // a ser el mismo mientras el conversationId no cambie).
            setClaimedFlag(conversationId);
            logger.debug(
              `[useProspectClaim] no prospect for session_id=${conversationId} (404)`,
            );
          } else if (status === 409) {
            // Otro user ya reclamó esta session. Race condition o
            // conversationId compartido entre devices. NO mostramos UI
            // para no romper UX. Marcamos flag para no loguear en loop.
            setClaimedFlag(conversationId);
            logger.warn(
              `[useProspectClaim] conflict on session_id=${conversationId} (otro user ya reclamó)`,
            );
          } else {
            // 401 / 5xx / network: NO seteamos flag — un próximo render
            // puede reintentar (ej. tras refresh de token).
            logger.warn(
              '[useProspectClaim] claim failed (will retry on next mount):',
              err instanceof Error ? err.message : String(err),
            );
          }
        } catch {
          // Caso extremo (mock roto / logger roto / acceso a getter que
          // throwea): swallow silencioso. Mejor perder un log que romper
          // la app o la suite de tests con un unhandled rejection.
        }
      } finally {
        // Liberamos el guard sincrónico — si en este tab el conversationId
        // cambia (logout + nueva sesión), el próximo render reintentará.
        if (inFlightRef.current === conversationId) {
          inFlightRef.current = null;
        }
      }
    })();
  }, [isAuthenticated, isLoading, conversationId]);
}
